import useRequest from "hooks/useRequest";
import { weightMetric } from "../../AthleteProfilingChart.config";
import UnitHelper from "helpers/UnitHelper";
import useOneRepMaxActions from "providers/OneRepMaxProvider/useOneRepMaxActions";
import useInfoMessage from "hooks/useInfoMessage";
import useUnitFormatter from "hooks/useUnitFormatter";
import { Fragment, useCallback } from "react";
import { Variants } from "types";
import { TooltipColorStrip, TooltipDataRow, TooltipPaper } from "components/TooltipCard/TooltipCard.components";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import OneRepMaxIcon from "components/Tables/icons/OneRepMaxIcon";
import Grid from "components/primitives/Grid";
import Button from "@mui/material/Button";
import useGetOneRepMaxFromStore from "hooks/useGetOneRepMaxFromStore";
import useColorRegistry from "features/Charting/hooks/useColorRegistry";
import { Trans, useTranslation } from "react-i18next";

export interface OneRepMaxEstimateTooltipProps {
  athleteId: string;
  exerciseId: string;
  estimatedOneRepMax: number;
  /** Already formatted with its unit. */
  minimumVelocityThreshold: string;
  /** The athlete's own MVT, already formatted, when it is below the exercise's and raised the estimate. */
  impliedMvt?: string;
  onSuccess(): void;
  variants: Variants[];
  entityId: string;
}

function OneRepMaxEstimateTooltip(props: OneRepMaxEstimateTooltipProps) {
  const {
    athleteId,
    exerciseId,
    estimatedOneRepMax,
    minimumVelocityThreshold,
    impliedMvt,
    variants,
    onSuccess,
    entityId,
  } = props;
  const formattedEstimatedOneRepMax = useUnitFormatter()(estimatedOneRepMax, {
    ...weightMetric,
    displayMetadata: { imperial: { decimals: 0, unit: UnitHelper.units.Pound }, metric: { decimals: 0 } },
  });
  const oneRepMaxActions = useOneRepMaxActions();
  const updateOneRepMax = useRequest(oneRepMaxActions.updateByAthleteIds, true);
  const successMessage = useInfoMessage({ type: "success" });
  const errorMessage = useInfoMessage({ type: "error" });
  const oneRepMaxValue = useGetOneRepMaxFromStore({ athleteId, exerciseId });
  const entityColor = useColorRegistry()(entityId);
  const { t } = useTranslation();
  const roundedEstimatedOneRepMax = Math.round(estimatedOneRepMax);

  const handleUpdateOneRepMax = useCallback(
    (variant: Variants | null) => async () => {
      try {
        await updateOneRepMax(exerciseId, [
          { athleteId, oneRepMaxes: [{ exerciseId, weight: roundedEstimatedOneRepMax, variant }] },
        ]);
        successMessage({ message: t("info.one_rep_max_updated_successfully") });
        onSuccess();
      } catch (error) {
        errorMessage({ message: t("errors.one_rep_max_update_failed") });
      }
    },
    [t, athleteId, errorMessage, roundedEstimatedOneRepMax, exerciseId, onSuccess, successMessage, updateOneRepMax]
  );

  const renderUpdateButton = useCallback(
    (variant: Variants | null, disabled?: boolean) => {
      return (
        <Grid pl={1}>
          <Button
            color="primary"
            variant="contained"
            size="small"
            onClick={handleUpdateOneRepMax(variant)}
            disableElevation
            disabled={disabled}
            sx={{ textTransform: "capitalize" }}
          >
            {t("charting.update_one_rep_max", {
              context: variant ? "variant" : null,
              variant: t("measures.variant", { context: variant }),
            })}
          </Button>
        </Grid>
      );
    },
    [t, handleUpdateOneRepMax]
  );

  // A single variant means the estimate is one side's, so only that side can be updated.
  const buttonVariants = variants.length ? variants : [null];
  const letter = variants.length === 1 ? t("measures.variant_single_letter", { context: variants[0] }) : undefined;

  return (
    <TooltipPaper>
      <TooltipColorStrip entityColor={entityColor} letter={letter} />
      <TooltipDataRow>
        <Grid container alignItems="center">
          <Grid lineHeight={0}>
            <OneRepMaxIcon />
          </Grid>
          <Grid size="grow" pl={1}>
            <Typography
              variant="h4"
              noWrap
              title={t("charting.one_rep_max_estimate_tooltip_title", {
                estimatedOneRepMax: formattedEstimatedOneRepMax,
              })}
            >
              <Trans
                i18nKey="charting.one_rep_max_estimate_tooltip"
                values={{ estimatedOneRepMax: formattedEstimatedOneRepMax }}
                components={[<Box component="span" sx={{ fontWeight: 500 }} />]}
              >
                Estimated 1RM -
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {formattedEstimatedOneRepMax}
                </Box>
              </Trans>
            </Typography>
          </Grid>
        </Grid>
      </TooltipDataRow>
      <Divider />
      <TooltipDataRow>
        <Typography
          sx={{
            color: "grey.800",
            fontWeight: "fontWeightNormal",
            fontSize: 12,
          }}
        >
          {Boolean(impliedMvt) ? (
            <Trans
              i18nKey="charting.one_rep_max_estimate_tooltip_implied_minimum_velocity_threshold"
              values={{ minimumVelocityThreshold }}
              components={{ bold: <Box component="span" sx={{ fontWeight: 500 }} /> }}
            />
          ) : (
            <Trans
              i18nKey="charting.one_rep_max_estimate_tooltip_minimum_velocity_threshold_formatted"
              values={{ minimumVelocityThreshold }}
              components={[<Box component="span" sx={{ fontWeight: 500 }} />]}
            >
              Estimate based on minimum velocity threshold of
              <Box component="span" sx={{ fontWeight: 500 }}>
                {minimumVelocityThreshold}
              </Box>
            </Trans>
          )}
        </Typography>
      </TooltipDataRow>
      <Divider />
      <TooltipDataRow>
        <Grid container alignItems="center">
          <Grid size="grow" />
          {buttonVariants.map((variant) => (
            <Fragment key={variant ?? "default"}>
              {renderUpdateButton(variant, oneRepMaxValue?.[variant ?? "default"] === roundedEstimatedOneRepMax)}
            </Fragment>
          ))}
        </Grid>
      </TooltipDataRow>
    </TooltipPaper>
  );
}

export default OneRepMaxEstimateTooltip;
